import { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
// import { useDate } from "../../hooks/useDate";

const useAuth = () => {
  const history = useHistory();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [socket, setSocket] = useState({});
  // Estado para o modal de troca de senha obrigatoria
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  useEffect(() => {
    // Definimos os interceptores uma única vez
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        const tokenToken = localStorage.getItem("token");
        if (tokenToken && tokenToken !== "undefined" && tokenToken !== "null") {
          try {
            config.headers["Authorization"] = `Bearer ${JSON.parse(tokenToken)}`;
          } catch (e) {
            console.error("Erro ao fazer parse do token", e);
            localStorage.removeItem("token");
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    let isRefreshing = false;
    let refreshPromise = null;

    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // 428 - Comunicados obrigatorios pendentes (gate do backend).
        // Nao usa 403 de proposito: 403 dispara o refresh_token acima.
        // Promise pendente: a tela fica bloqueada pelo modal ate o aceite, que recarrega a pagina.
        if (
          error?.response?.status === 428 &&
          error?.response?.data?.error === "PENDING_NOTIFICATIONS"
        ) {
          window.dispatchEvent(new Event("system-notices:pending"));
          return new Promise(() => {});
        }

        // 403 Error - Likely Token Expired
        if (
          error?.response?.status === 403 &&
          !originalRequest._retry &&
          !originalRequest.url?.endsWith("/auth/refresh_token") &&
          !(typeof error?.response?.data?.error === "string" && error.response.data.error.startsWith("ERR_") && error.response.data.error !== "ERR_SESSION_EXPIRED")
        ) {
          originalRequest._retry = true;

          if (!isRefreshing) {
            isRefreshing = true;
            refreshPromise = api.post("/auth/refresh_token")
              .then(({ data }) => {
                isRefreshing = false;
                if (data) {
                  localStorage.setItem("token", JSON.stringify(data.token));
                  api.defaults.headers.Authorization = `Bearer ${data.token}`;
                  return data.token;
                }
                throw new Error("Refresh failed");
              })
              .catch((err) => {
                isRefreshing = false;
                const st = err?.response?.status;
                // Erro de rede/502/503 (backend reiniciando) NAO pode derrubar o login
                if (st === 401 || st === 403) {
                  localStorage.removeItem("token");
                  api.defaults.headers.Authorization = undefined;
                  setIsAuth(false);
                }
                throw err;
              });
          }

          try {
            const newToken = await refreshPromise;
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (err) {
            const st = err?.response?.status;
            if (st === 401 || st === 403) {
              localStorage.removeItem("token");
              api.defaults.headers.Authorization = undefined;
              setIsAuth(false);
              if (!window.location.pathname.includes("/login")) {
                window.location.href = "/login";
              }
              // Retorna promise pendente para requisições em background não dispararem
              // "Uncaught runtime errors" no overlay do Create React App em desenvolvimento
              return new Promise(() => {});
            }
            return Promise.reject(err);
          }
        }

        // 401 Error - Unauthorized
        if (error?.response?.status === 401) {
          localStorage.removeItem("token");
          api.defaults.headers.Authorization = undefined;
          setIsAuth(false);
          
          if (!window.location.pathname.includes("/login")) {
            window.location.href = "/login";
            return new Promise(() => {});
          }

          // Se já está na tela de login mas é uma requisição secundária de fundo (não a submissão do form)
          if (!originalRequest?.url?.endsWith("/auth/login")) {
            return new Promise(() => {});
          }
        }

        return Promise.reject(error);
      }
    );

    const token = localStorage.getItem("token");
    (async () => {
      if (token && token !== "undefined" && token !== "null") {
        try {
          // Marcamos como isBackground para o interceptor saber que não deve causar overlay/redirecionamento
          const { data } = await api.post("/auth/refresh_token", {}, { isBackground: true });
          if (data && data.token) {
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            setIsAuth(true);
            setUser(data.user);
            if (data.user?.isDefaultPassword) {
              setShowPasswordModal(true);
            }
          }
        } catch (err) {
          const st = err?.response?.status;
          if (st === 401 || st === 403) {
            // Sessão realmente inválida/expirada
            setIsAuth(false);
            localStorage.removeItem("token");
            if (st === 401) {
              localStorage.clear();
              history.push("/login");
            }
          } else {
            // Backend fora/lento (502, timeout, CORS de proxy): mantém o token e tenta de novo
            console.warn("[Auth] Refresh falhou sem resposta de auth, mantendo sessão e tentando novamente", err?.message);
            for (const waitMs of [2000, 5000, 10000]) {
              await new Promise((r) => setTimeout(r, waitMs));
              try {
                const { data } = await api.post("/auth/refresh_token", {}, { isBackground: true });
                if (data && data.token) {
                  localStorage.setItem("token", JSON.stringify(data.token));
                  api.defaults.headers.Authorization = `Bearer ${data.token}`;
                  setIsAuth(true);
                  setUser(data.user);
                  if (data.user?.isDefaultPassword) {
                    setShowPasswordModal(true);
                  }
                  break;
                }
              } catch (e2) {
                const s2 = e2?.response?.status;
                if (s2 === 401 || s2 === 403) {
                  setIsAuth(false);
                  localStorage.removeItem("token");
                  break;
                }
              }
            }
          }
        }
      }
      setLoading(false);
    })();

    // Cleanup dos interceptores ao desmontar a AuthProvider
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [history]);

  useEffect(() => {
    if (!isAuth) return;

    // Renovação proativa do token a cada 12 minutos (720.000 ms) para evitar erros 403 com a aba parada
    const interval = setInterval(async () => {
      try {
        const { data } = await api.post("/auth/refresh_token", {}, { isBackground: true });
        if (data && data.token) {
          localStorage.setItem("token", JSON.stringify(data.token));
          api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }
      } catch (err) {
        console.warn("[Auth] Erro na renovação proativa do token:", err?.message);
      }
    }, 12 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isAuth]);

  // Ref para ler user.id dentro do handler sem re-registrar o listener
  const userIdRef = useRef(user.id);
  userIdRef.current = user.id;

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      const io = socketConnection({ user });
      setSocket(io);

      io.emit("joinUser", user.id);
      if (user.profile === "admin" || user.profile === "supervisor") {
        io.emit("joinAdmins");
      }

      const companyId = user.companyId || user.company_id;
      io.on(`company-${companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === userIdRef.current) {
          setUser((prev) => ({
            ...prev,
            ...data.user,
            // Previne a perda de permissões quando o socket envia um payload parcial (ex: online status)
            departments: data.user.departments !== undefined ? data.user.departments : prev.departments,
            queues: data.user.queues !== undefined ? data.user.queues : prev.queues,
            whatsappIds: data.user.whatsappIds !== undefined ? data.user.whatsappIds : prev.whatsappIds,
            tags: data.user.tags !== undefined ? data.user.tags : prev.tags
          }));
        }
      });

      return () => {
        io.off(`company-${companyId}-user`);
      };
    }
    // Usar user.id em vez de user para evitar loop: setUser → novo user → re-register
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.profile]);

  const dummySocket = {
    on: () => {},
    off: () => {},
    emit: () => {},
    disconnect: () => {}
  };
  const socketActive = Object.keys(socket).length ? socket : (Object.keys(user).length ? socketConnection({ user }) : dummySocket);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", null); //regra pra exibir campanhas
        }
      }

      if (has(company, "companieSettings") && isArray(company.companieSettings[0])) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable); //regra pra exibir campanhas
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage); //regra pra exibir imagem contato

      // Bloqueio por atraso é decidido no backend (billingBlocked no login e 402 nas
      // rotas). Aqui só guardamos a sessão; o aviso de vencimento fica no BillingGate.
      if (data.user.company?.dueDate) {
        const vencimento = new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric"
        }).format(new Date(`${String(data.user.company.dueDate).slice(0, 10)}T12:00:00`));
        localStorage.setItem("companyDueDate", vencimento);
      }

      localStorage.setItem("token", JSON.stringify(data.token));
      api.defaults.headers.Authorization = `Bearer ${data.token}`;
      setUser(data.user);
      setIsAuth(true);
      setLoading(false);

      if (data.billingBlocked) {
        // Empresa em atraso: só o admin chega aqui, e vai direto para pagar.
        history.push("/billing/pay");
        return;
      }

      toast.success(i18n.t("auth.toasts.success"));

      // Se a senha atual e a padrao, abrir modal de troca antes de redirecionar
      if (data.user.isDefaultPassword) {
        setShowPasswordModal(true);
      } else {
        history.push("/tickets");
      }

    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
    } catch (err) {
      console.log("Logout error (likely already logged out on server):", err);
    } finally {
      setIsAuth(false);
      setUser({});
      localStorage.clear(); // 🛡️ Limpeza total de segurança
      api.defaults.headers.Authorization = undefined;
      setLoading(false);
      history.push("/login");
    }
  };

  const getCurrentUserInfo = async () => {
    try {
      const { data } = await api.get("/auth/me");
      console.log(data)
      return data;
    } catch (_) {
      return null;
    }
  };

  return {
    isAuth,
    user,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    showPasswordModal,
    setShowPasswordModal,
    socket: socketActive
  };
};

export default useAuth;
